import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

import { ColoredBadge } from "@/components/common/colored-badge";
import { UserAvatar } from "@/components/common/user-avatar";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  makeTooltip,
} from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { enrichmentSourceLabel } from "@/lib/tina/enrichment-jobs";
import { RunStatusBadge } from "@/pages/tina/components/run-status-badge";
import {
  resolveTriggerActor,
  triggeredByLabel,
} from "@/pages/tina/lib/run-attribution";
import {
  enrichmentRunSpec,
  refreshRunHourLabel,
  refreshRunSpec,
} from "@/pages/tina/runs/runs-filters";
import type { RunRead, TriggerSpec } from "@/types/tina";

const SITES_PREVIEW = 3;

const triggerSites = (spec: TriggerSpec): string[] =>
  spec && (spec.type === "manual" || spec.type === "cron")
    ? (spec.sites ?? [])
    : [];

export const summarizeTriggerSpec = (spec: TriggerSpec): string => {
  if (!spec) return "";
  // An enrichment run is a `manual` trigger whose only site is
  // the `system-enrichment` sentinel, so the generic branch below rendered
  // "manual · 1 site" for all of them — true and useless. Render what actually
  // varies: who asked, and for how many entities.
  const enrichment = enrichmentRunSpec(spec);
  if (enrichment) {
    const count = enrichment.entityCount;
    return [
      "enrichment",
      enrichment.source ? enrichmentSourceLabel(enrichment.source) : null,
      count === null
        ? null
        : `${count.toLocaleString()} entit${count === 1 ? "y" : "ies"}`,
    ]
      .filter((part): part is string => Boolean(part))
      .join(" · ");
  }
  // Without this branch a refresh spec falls to `(spec as TriggerSpec).type`
  // below and renders the bare word "refresh" — true and useless, same failure
  // mode the enrichment branch above exists to avoid. The hour is the run's
  // identity (the sweep does a find-or-create on it), so lead with that.
  const refresh = refreshRunSpec(spec);
  if (refresh) {
    const sites = refresh.sites.length;
    return [
      `refresh · ${refreshRunHourLabel(spec)}`,
      sites ? `${sites} carry site${sites === 1 ? "" : "s"}` : null,
    ]
      .filter((part): part is string => Boolean(part))
      .join(" · ");
  }
  if (spec.type === "manual") {
    const sites = spec.sites?.length ?? 0;
    return `manual · ${sites} site${sites === 1 ? "" : "s"}${spec.limit ? ` · limit ${spec.limit}` : ""}`;
  }
  if (spec.type === "cron") {
    const sites = spec.sites?.length ?? 0;
    return sites ? `cron · ${sites} site${sites === 1 ? "" : "s"}` : "cron";
  }
  return (spec as TriggerSpec).type;
};

const TRIGGER_COLORS: Record<string, string> = {
  manual: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  cron: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
};

const formatDuration = (
  started: string | null,
  finished: string | null,
): string => {
  if (!started) return "—";
  const end = finished ? new Date(finished) : new Date();
  const ms = end.getTime() - new Date(started).getTime();
  if (ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  return `${min}m ${sec % 60}s`;
};

/**
 * `runs` is the scrape tab (unchanged); `enrichment` and `refresh` each drop
 * the columns that are constant for their class and add the ones that vary.
 *
 * The enrichment tab drops two columns that are constant there — `trigger_type`
 * is `manual` on every enrichment run (there is no enrichment trigger type) and
 * `sites` is always the single `system-enrichment` sentinel — and puts `source`
 * and the entity count in their place, because `backend-create` vs `ims` vs
 * `bff-onboarding` is the axis that actually varies.
 *
 * The refresh tab drops `trigger_type` too — the sweep rides the cron tick, so
 * it is always `cron` — and replaces `sites` with **Hour**: `trigger_spec.sites`
 * on a refresh run is only ever the carry-site seed the sweep crawled, not a
 * per-job site, while `refresh_run_hour_key` (the sweep's find-or-create key,
 * so every job seeded that hour — including a daytime duplicate-hook job —
 * lands in this run) is the one thing that actually identifies the row. There
 * is deliberately no Jobs/count column here: `RunRead` carries no per-run job
 * count and `v_run_summary` is not fetched by this list; the count lives on
 * the run show page instead.
 */
export type RunsColumnVariant = "runs" | "enrichment" | "refresh";

/** `source` / `entities` — the enrichment tab's two replacement columns. */
const ENRICHMENT_RUN_COLUMNS: ColumnDef<RunRead>[] = [
  {
    id: "source",
    header: "Source",
    // No server param: `GET /runs` filters on status/trigger_type/triggered_by/
    // job_kind/site/since only, and the Tina data provider forwards bare `eq`
    // filters as query params — an unrecognised one is silently ignored, which
    // is worse than no control. Display column, honestly.
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const spec = enrichmentRunSpec(row.original.trigger_spec);
      if (!spec?.source)
        return <span className="text-muted-foreground text-xs">—</span>;
      return makeTooltip(
        <Badge variant="secondary" className="text-[10px] font-normal">
          {enrichmentSourceLabel(spec.source)}
        </Badge>,
        spec.requestedBy
          ? `${spec.source} · requested by ${spec.requestedBy}`
          : spec.source,
      );
    },
  },
  {
    id: "entity_count",
    header: "Entities",
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const spec = enrichmentRunSpec(row.original.trigger_spec);
      if (spec?.entityCount === null || spec?.entityCount === undefined)
        return <span className="text-muted-foreground text-xs">—</span>;
      return (
        <span className="font-mono text-xs">
          {spec.entityCount.toLocaleString()}
        </span>
      );
    },
  },
];

/** `hour` — the refresh tab's replacement for `sites`. */
const REFRESH_RUN_COLUMNS: ColumnDef<RunRead>[] = [
  {
    id: "hour",
    header: "Hour",
    // Same reasoning as ENRICHMENT_RUN_COLUMNS.source: `hour` lives inside
    // `trigger_spec`, which `GET /runs` cannot filter or sort on, so a control
    // here would silently no-op against the server. Display column, honestly.
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => {
      const label = refreshRunHourLabel(row.original.trigger_spec);
      if (!label)
        return <span className="text-muted-foreground text-xs">—</span>;
      const rawHour = refreshRunSpec(row.original.trigger_spec)?.hour;
      return makeTooltip(
        <span className="font-mono text-xs">{label}</span>,
        rawHour ?? undefined,
      );
    },
  },
];

export const useRunsColumns = (options?: { variant?: RunsColumnVariant }) => {
  // Destructured to a string before it reaches the memo deps — an object literal
  // from the caller is a fresh reference every render.
  const variant: RunsColumnVariant = options?.variant ?? "runs";
  const { members } = useMembers();
  return React.useMemo<ColumnDef<RunRead>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) =>
          makeTooltip(
            <span className="font-mono text-xs font-medium">
              {row.original.id.slice(0, 8)}
            </span>,
            row.original.id,
          ),
        enableColumnFilter: false,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <RunStatusBadge status={row.original.status} />,
        meta: {
          filterType: "select",
          options: [
            { label: "Queued", value: "queued" },
            { label: "Running", value: "running" },
            { label: "Done", value: "done" },
            { label: "Failed", value: "failed" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
      },
      // Constant on both non-scrape classes: `manual` on every enrichment run,
      // `cron` on every refresh run (the sweep rides the cron tick) — see the
      // `RunsColumnVariant` doc comment.
      ...(variant === "runs"
        ? [
            {
              id: "trigger_type",
              accessorKey: "trigger_type",
              header: "Trigger",
              cell: (props) => (
                <ColoredBadge
                  getValue={props.getValue}
                  colors={TRIGGER_COLORS}
                />
              ),
              meta: {
                filterType: "select",
                options: [
                  { label: "Manual", value: "manual" },
                  { label: "Cron", value: "cron" },
                ],
              },
            } as ColumnDef<RunRead>,
          ]
        : []),
      {
        id: "trigger_spec",
        accessorKey: "trigger_spec",
        header: "Spec",
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {summarizeTriggerSpec(row.original.trigger_spec)}
          </span>
        ),
        enableColumnFilter: false,
      },
      ...(variant === "enrichment"
        ? ENRICHMENT_RUN_COLUMNS
        : variant === "refresh"
          ? REFRESH_RUN_COLUMNS
          : ([
              {
                id: "sites",
                header: "Sites",
                enableSorting: false,
                enableColumnFilter: false,
                cell: ({ row }) => {
                  const names = triggerSites(row.original.trigger_spec);
                  if (names.length === 0)
                    return (
                      <span className="text-muted-foreground text-xs">—</span>
                    );

                  const preview = names.slice(0, SITES_PREVIEW);
                  const extra = names.length - preview.length;

                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex max-w-[280px] flex-wrap items-center gap-1">
                          {preview.map((name) => (
                            <Badge
                              key={name}
                              variant="secondary"
                              className="max-w-[140px] truncate text-[10px] font-normal"
                            >
                              {name}
                            </Badge>
                          ))}
                          {extra > 0 && (
                            <span className="text-muted-foreground text-xs">
                              +{extra}
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs whitespace-pre-line">
                        {names.join("\n")}
                      </TooltipContent>
                    </Tooltip>
                  );
                },
              },
            ] as ColumnDef<RunRead>[])),
      {
        id: "duration",
        header: "Duration",
        cell: ({ row }) => {
          const startedTip = row.original.started_at
            ? dayjs(row.original.started_at).format("DD/MM/YYYY HH:mm")
            : "—";
          const finishedTip = row.original.finished_at
            ? dayjs(row.original.finished_at).format("DD/MM/YYYY HH:mm")
            : "—";
          return makeTooltip(
            <span className="font-mono text-xs">
              {formatDuration(
                row.original.started_at,
                row.original.finished_at,
              )}
            </span>,
            `started ${startedTip} · finished ${finishedTip}`,
          );
        },
        enableColumnFilter: false,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        accessorFn: (row) => dayjs(row.created_at),
        cell: function render({ getValue }: any) {
          return makeTooltip(
            getValue().format("DD/MM/YYYY HH:mm"),
            getValue().format("ddd, MMM D, YYYY HH:mm"),
          );
        },
        enableColumnFilter: false,
      },
      {
        id: "triggered_by",
        accessorKey: "triggered_by",
        header: "Triggered by",
        cell: ({ row }) => {
          const actor = resolveTriggerActor(
            row.original.triggered_by,
            row.original.trigger_type,
            members,
          );
          return actor ? (
            <div className="p-1">
              <UserAvatar {...actor} half />
            </div>
          ) : (
            <span className="text-muted-foreground text-xs">
              {triggeredByLabel(
                row.original.triggered_by,
                row.original.trigger_type,
                members,
              )}
            </span>
          );
        },
        meta: {
          filterType: "select",
          options: members.map((m) => ({
            label: m.displayName || m.email || m.id,
            value: m.id,
          })),
        },
      },
      {
        id: "actions",
        size: 70,
        header: "Actions",
        cell: ({ row }) => (
          <ShowButton size="sm" recordItemId={row.original.id} hideText />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [members, variant],
  );
};
